import { api } from './api'
import type {
  KrakenAccess,
  KrakenAccount,
  KrakenBalance,
  KrakenConnection,
  KrakenOpenOrder,
  KrakenTicket,
  LatestSignal,
  LiveRecord,
  LiveTrade,
} from '../types/kraken'

// Live Kraken trading — real money, superusers only. Every rail is enforced
// server-side; nothing here can skip a pre-check.
export const krakenAPI = {
  access: async () => (await api.get<KrakenAccess>('/api/kraken/access')).data,

  connection: async () => (await api.get<KrakenConnection>('/api/kraken/connection')).data,

  connect: async (body: { api_key: string; api_secret: string; confirm_no_withdrawal_permissions: boolean }) =>
    (await api.post<{ connected: boolean; key_hint: string }>('/api/kraken/connection', body)).data,

  disconnect: async (force = false) =>
    (await api.delete('/api/kraken/connection', { params: { force } })).data,

  balance: async () => (await api.get<KrakenBalance>('/api/kraken/balance')).data,

  // Every holding and open order on the Kraken account, FinCreator's or not.
  account: async () => (await api.get<KrakenAccount>('/api/kraken/account')).data,

  ticket: async (signal_id: number, usd_amount: number) =>
    (await api.post<KrakenTicket>('/api/kraken/ticket', { signal_id, usd_amount })).data,

  validate: async (signal_id: number, usd_amount: number) =>
    (await api.post<KrakenTicket>('/api/kraken/trades/validate', { signal_id, usd_amount })).data,

  // Kraken can take a few seconds; the response is Kraken's actual answer.
  confirm: async (trade_id: number) =>
    (
      await api.post<LiveTrade>(
        `/api/kraken/trades/${trade_id}/confirm`,
        { acknowledge_real_money: true },
        { timeout: 60_000 }
      )
    ).data,

  trades: async (view: 'open' | 'pending' | 'history' | 'all') =>
    (await api.get<{ trades: LiveTrade[] }>('/api/kraken/trades', { params: { view } })).data.trades,

  trade: async (id: number) => (await api.get<LiveTrade>(`/api/kraken/trades/${id}`)).data,

  refresh: async (id: number) => (await api.post<LiveTrade>(`/api/kraken/trades/${id}/refresh`)).data,

  moveStop: async (id: number, price: number) =>
    (await api.post<LiveTrade>(`/api/kraken/trades/${id}/stop`, { price })).data,

  changeTarget: async (id: number, price: number) =>
    (await api.post<LiveTrade>(`/api/kraken/trades/${id}/target`, { price })).data,

  cancelEntry: async (id: number) => (await api.post<LiveTrade>(`/api/kraken/trades/${id}/cancel-entry`)).data,

  close: async (id: number) =>
    (await api.post<LiveTrade>(`/api/kraken/trades/${id}/close`, undefined, { timeout: 60_000 })).data,

  openOrders: async () => (await api.get<{ orders: KrakenOpenOrder[] }>('/api/kraken/open-orders')).data.orders,

  record: async () => (await api.get<LiveRecord>('/api/kraken/record')).data,

  latestSignal: async () => (await api.get<{ signal: LatestSignal | null }>('/api/kraken/latest-signal')).data.signal,
}

/** The backend's refusal message ({detail: {code, message}}) or a fallback. */
export const krakenError = (error: unknown, fallback: string): string => {
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
  if (typeof detail === 'string' && detail.trim()) return detail
  if (detail && typeof detail === 'object' && 'message' in detail) {
    const message = (detail as { message?: unknown }).message
    if (typeof message === 'string' && message.trim()) return message
  }
  return fallback
}

/** The machine-readable refusal code, when the backend sent one. */
export const krakenErrorCode = (error: unknown): string | null => {
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
  if (detail && typeof detail === 'object' && 'code' in detail) {
    const code = (detail as { code?: unknown }).code
    return typeof code === 'string' ? code : null
  }
  return null
}
